import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from '../apiClient';
import {
  Company,
  CreateExternalCompany,
  CreateCompanyResponse,
  CreateAddressPayload,
  CreateCompanyAddressResponse,
  CreateLocationPayload,
  OrganizationDetailSectionFormData,
  DataSharingPayload,
  OrganizationModalApprove,
  CompanyAddress,
  AssignableCompany,
  UploadLocationImageResponse,
} from 'types/organizationTypes';
import { PaginationQueryParams } from 'types/queryTypes';
import { useUI } from 'contexts/UiContext';
import { SortOrder } from 'hooks/useTableControls';

// Base API URL

const REACT_APP_DARWIN_URL = process.env.REACT_APP_DARWIN_URL;

// Query keys
const ORGANIZATIONS_QUERY_KEY = 'organizations';
const ORGANIZATION_NAME_QUERY = 'organizationName';
const SINGLE_ORGANIZATION_QUERY_KEY = 'organization';
const ADDRESSES_QUERY_KEY = 'addresses';
const LOCATIONS_IMAGE_QUERY_KEY = 'locations_image';

/**
 * Fetch all organizations.
 */
export const useGetAllOrganizations = ({
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'name', order: 'ASC' },
}: PaginationQueryParams & {
  sortOrder: SortOrder;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<Company[]>(
        `${REACT_APP_DARWIN_URL}/companies/${selectedCompany}/related-companies?limit=${limit}&page=${page}&filter=${query}&sortBy=${sortOrder.sortBy}&order=${sortOrder.order}`
      ),
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch organizations by name.
 */
export const useGetOrganizationsByName = ({ name = '' }: { name: string }) => {
  const { selectedCompany } = useUI();
  return useQuery({
    queryKey: [ORGANIZATION_NAME_QUERY, name],
    queryFn: () =>
      fetchFromApi<OrganizationModalApprove[]>(
        `${REACT_APP_DARWIN_URL}/companies/${selectedCompany}/related-companies/search?name=${name}`
      ),
    refetchOnWindowFocus: false,
  });
};

/**
 * Approve data sharing request
 */
export const useApproveDataSharingRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      targetCompanyId,
      token,
      associatedCompanyId = '',
    }: {
      companyId: string | number;
      targetCompanyId: string | number;
      token: string | number;
      associatedCompanyId?: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/data-sharing/${companyId}/target-company/${targetCompanyId}/approve?token=${token}${
          !!associatedCompanyId
            ? `&associatedCompanyId=${associatedCompanyId}`
            : ''
        }`,
        {
          method: 'GET',
        }
      ),
    onSuccess: (_, { targetCompanyId }) => {
      // Invalidate the cache of the organization list and the updated organization
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [SINGLE_ORGANIZATION_QUERY_KEY, targetCompanyId],
      });
    },
    onError: (_, { targetCompanyId }) => {
      // Invalidate the cache of the organization list and the specific organization
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [SINGLE_ORGANIZATION_QUERY_KEY, targetCompanyId],
      });
    },
  });
};

/**
 * Fetch company alliances for a given company.
 */
export const useGetCompaniesAlliances = ({
  companyId,
  limit = 10,
  page = 1,
}: {
  companyId: string | number;
  limit?: number;
  page?: number;
}) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, companyId, limit, page],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/allied-organizations?limit=${limit}&page=${page}`
      ),
    enabled: !!companyId,
  });
};

/**
 * Fetch all assignable organizations for company.
 */
export const useGetAssignableOrganizationsForCompany = (
  companyId: string | number
) =>
  useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, companyId, 'selector'],
    queryFn: () =>
      fetchFromApi<AssignableCompany[]>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/selector`
      ),
    refetchOnWindowFocus: false,
  });

/**
 * Create an external organization (without key).
 */
export const useCreateExternalOrganizationWithoutKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyData,
      companyId,
    }: {
      companyData: CreateExternalCompany;
      companyId: string | number;
    }) =>
      fetchFromApi<CreateCompanyResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/external`,
        {
          method: 'POST',
          body: JSON.stringify(companyData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
    },
  });
};

/**
 * Create an address for an external organization.
 */
export const useCreateAddressForExternalOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      addressData,
      companyId,
      targetCompanyId,
    }: {
      addressData: CreateAddressPayload;
      companyId: string | number;
      targetCompanyId: string | number;
    }) =>
      fetchFromApi<CreateCompanyAddressResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses`,
        {
          method: 'POST',
          body: JSON.stringify(addressData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESSES_QUERY_KEY] });
    },
  });
};

/**
 * Fetch all addresses for a specific company.
 */
export const useGetAddressesForCompany = ({
  companyId,
  targetCompanyId,
  limit = 10,
  page = 1,
}: {
  companyId: string | number;
  targetCompanyId: string | number;
} & PaginationQueryParams) =>
  useQuery({
    queryKey: [ADDRESSES_QUERY_KEY, companyId, targetCompanyId],
    queryFn: () =>
      fetchFromApi<CompanyAddress[]>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses?limit=${limit}&page=${page}`
      ),
    refetchOnWindowFocus: false,
    enabled: !!companyId && !!targetCompanyId,
  });

/**
 * Create a location for an specific address that belongs to an external organization.
 */
export const useCreateLocationForAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationData,
      companyId,
      targetCompanyId,
      addressId,
    }: {
      locationData: CreateLocationPayload;
      companyId: string | number;
      targetCompanyId: string | number;
      addressId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses/${addressId}/locations`,
        {
          method: 'POST',
          body: JSON.stringify(locationData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ADDRESSES_QUERY_KEY],
      });
    },
  });
};

/**
 * Get organization details by id.
 */
export const useGetTargetCompanyOrganizationById = (
  companyId: string,
  targetCompanyId: string
) => {
  return useQuery({
    queryKey: [SINGLE_ORGANIZATION_QUERY_KEY, companyId, targetCompanyId],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}`
      ),
    enabled: !!companyId && !!targetCompanyId,
  });
};

/**
 * Update an external organization (without key).
 */
export const useUpdateExternalOrganizationWithoutKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyData,
      companyId,
      targetCompanyId,
    }: {
      companyData: CreateExternalCompany;
      companyId: string | number;
      targetCompanyId: string | number;
    }) =>
      fetchFromApi<CreateCompanyResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}`,
        {
          method: 'PUT',
          body: JSON.stringify(companyData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
    },
  });
};

/**
 * Update an address for an external organization (without key).
 */
export const useUpdateAddressForExternalOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      addressData,
      companyId,
      targetCompanyId,
      addressId,
    }: {
      addressData: CreateAddressPayload;
      companyId: string | number;
      targetCompanyId: string | number;
      addressId: string | number;
    }) =>
      fetchFromApi<CreateCompanyResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses/${addressId}`,
        {
          method: 'PUT',
          body: JSON.stringify(addressData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESSES_QUERY_KEY] });
    },
  });
};

/**
 * Update a location for an specific address that belongs to an external organization.
 */
export const useUpdateLocationForAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationData,
      companyId,
      targetCompanyId,
      addressId,
      locationId,
    }: {
      locationData: CreateLocationPayload;
      companyId: string | number;
      targetCompanyId: string | number;
      addressId: string | number;
      locationId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses/${addressId}/locations/${locationId}`,
        {
          method: 'PUT',
          body: JSON.stringify(locationData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ADDRESSES_QUERY_KEY],
      });
    },
  });
};

/**
 * Delete an address for an external organization (without key).
 */
export const useDeleteAddressForExternalOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      targetCompanyId,
      addressId,
    }: {
      companyId: string | number;
      targetCompanyId: string | number;
      addressId: string | number;
    }) =>
      fetchFromApi<CreateCompanyResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses/${addressId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESSES_QUERY_KEY] });
    },
  });
};

/**
 * Delete a location for an specific address that belongs to an external organization.
 */
export const useDeleteLocationForAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      targetCompanyId,
      addressId,
      locationId,
    }: {
      companyId: string | number;
      targetCompanyId: string | number;
      addressId: string | number;
      locationId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/addresses/${addressId}/locations/${locationId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ADDRESSES_QUERY_KEY],
      });
    },
  });
};

/**
 * Check if a data sharing request is possible between two organizations.
 */
export const useCheckIfDataSharingRequestIsPossible = (
  companyId: string,
  targetCompanyOrganizationKey: string
) => {
  return useQuery({
    queryKey: [
      ORGANIZATIONS_QUERY_KEY,
      companyId,
      targetCompanyOrganizationKey,
    ],
    queryFn: () =>
      fetchFromApi<OrganizationDetailSectionFormData>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/exists-primary-company/${targetCompanyOrganizationKey}`
      ),
    enabled: !!companyId && !!targetCompanyOrganizationKey,
    retry: 0,
  });
};

/**
 * Get company data to request a data sharing
 */
export const useGetCompanyDataForDataSharingRequest = (companyId: string) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, companyId],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/primary-address`
      ),
    enabled: !!companyId,
  });
};

/**
 * Create a data sharing request
 */
export const useCreateDataSharingRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dataSharingPayload,
      companyId,
      targetCompanyId,
    }: {
      dataSharingPayload: DataSharingPayload;
      companyId: string;
      targetCompanyId: string;
    }) =>
      fetchFromApi<CreateCompanyResponse>(
        `${REACT_APP_DARWIN_URL}/data-sharing/${companyId}/target-company/${targetCompanyId}`,
        {
          method: 'POST',
          body: JSON.stringify(dataSharingPayload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
    },
  });
};

/**
 * Get auto-complete contact
 */
export const useGetContactAutoComplete = ({
  companyId,
  targetCompanyId,
  search,
}: {
  companyId: string;
  targetCompanyId: string;
  search: string;
}) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, companyId, targetCompanyId, search],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}/contacts/search?filter=${search}`
      ),
    enabled: !!companyId && !!targetCompanyId,
  });
};

/**
 * Delete an organization.
 */
export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      targetCompanyId,
    }: {
      companyId: string | number;
      targetCompanyId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/target-company/${targetCompanyId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: (_, { targetCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [SINGLE_ORGANIZATION_QUERY_KEY, targetCompanyId],
      });
    },
  });
};

/**
 * Upload Location Img
 */
export const useUploadLocationImg = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      folder = 'locations',
    }: {
      file: File;
      folder?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);

      return fetchFromApi<UploadLocationImageResponse>(
        `${REACT_APP_DARWIN_URL}/files/upload/${folder}`,
        {
          method: 'POST',
          body: formData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_IMAGE_QUERY_KEY] });
    },
  });
};
